import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import useWorldStore, { getPrimaryCallParticipant } from "../store/worldStore"
import useFloatingWindowLayer from "../hooks/useFloatingWindowLayer"
import {
  getChatAppConnectionIndicator,
  getDefaultChatAppConnectionStatus,
} from "../lib/chatAppEmbed"
import {
  shouldSkipPointerDragStart,
  startPointerSession,
} from "../../../lib/pointer-drag"

import ChatAppEmbedFrame from "./ChatAppEmbedFrame"

const IC = {
  phone: "📞",
  expand: "⛶",
  shrink: "⊡",
  dash: "−",
  x: "×",
}

function ConnectionBadge({ indicator }) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "3px 8px",
        borderRadius: 999,
        background: "rgba(255,255,255,0.08)",
        border: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      <span
        aria-hidden
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: indicator.color,
          boxShadow: `0 0 12px ${indicator.color}`,
          flexShrink: 0,
        }}
      />
      <span
        style={{
          fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
          fontSize: 10,
          color: "rgba(255,255,255,0.82)",
          letterSpacing: "0.03em",
        }}
      >
        {indicator.label}
      </span>
    </div>
  )
}

export default function CallWindow() {
  const calls = useWorldStore((s) => s.activeCalls || [])
  const agents = useWorldStore((s) => s.agents)
  const startCall = useWorldStore((s) => s.startCall)
  const endCall = useWorldStore((s) => s.endCall)

  useEffect(() => {
    const numpadMap = {
      Numpad1: agents[0]?.id,
      Numpad2: agents[1]?.id,
      Numpad3: agents[2]?.id,
    }

    const handler = (e) => {
      const agentId = numpadMap[e.code]
      if (!agentId) return
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA")
        return

      const currentCalls = useWorldStore.getState().activeCalls || []
      const existingCall = currentCalls.find(
        (call) => getPrimaryCallParticipant(call) === agentId
      )
      if (existingCall && !existingCall.minimized) {
        endCall(agentId)
        return
      }

      startCall(agentId)
    }

    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [agents, endCall, startCall])

  return calls.map((call, index) => (
    <CallWindowFrame
      key={call.agentId || getPrimaryCallParticipant(call) || `call-${index}`}
      agents={agents}
      call={call}
    />
  ))
}

function CallWindowFrame({ call, agents }) {
  const endCall = useWorldStore((s) => s.endCall)
  const toggleMin = useWorldStore((s) => s.toggleCallMinimized)
  const toggleFs = useWorldStore((s) => s.toggleCallFullscreen)
  const setPos = useWorldStore((s) => s.setCallPosition)
  const setSize = useWorldStore((s) => s.setCallSize)
  const dragRef = useRef(null)
  const windowId = call ? `call:${getPrimaryCallParticipant(call)}` : null
  const {
    zIndex: layeredZIndex,
    focusProps,
    focusWindow,
  } = useFloatingWindowLayer(windowId)

  const activeAgentId = getPrimaryCallParticipant(call)
  const activeAgent = useMemo(
    () => agents.find((agent) => agent.id === activeAgentId) ?? null,
    [activeAgentId, agents]
  )
  const [connectionStatus, setConnectionStatus] = useState(() =>
    getDefaultChatAppConnectionStatus()
  )
  const connectionIndicator = useMemo(
    () => getChatAppConnectionIndicator(connectionStatus),
    [connectionStatus]
  )

  useEffect(() => {
    setConnectionStatus(getDefaultChatAppConnectionStatus())
  }, [activeAgentId])

  const onDragStart = useCallback(
    (e) => {
      if (
        !call ||
        call.fullscreen ||
        !activeAgentId ||
        shouldSkipPointerDragStart(e)
      )
        return
      focusWindow()

      const startX = e.clientX
      const startY = e.clientY
      const startPos = call.minimized
        ? (dragRef.current ?? { x: 16, y: window.innerHeight - 176 })
        : call.position
      dragRef.current = startPos

      startPointerSession(e, {
        onMove: (ev) => {
          const nx = Math.max(
            0,
            Math.min(window.innerWidth - 160, startPos.x + ev.clientX - startX)
          )
          const ny = Math.max(
            0,
            Math.min(window.innerHeight - 60, startPos.y + ev.clientY - startY)
          )
          setPos(activeAgentId, nx, ny)
        },
      })
    },
    [activeAgentId, call, focusWindow, setPos]
  )

  const onResizeStart = useCallback(
    (e) => {
      if (!call || call.fullscreen || call.minimized || !activeAgentId) return
      e.stopPropagation()

      const startX = e.clientX
      const startY = e.clientY
      const startW = call.size.w
      const startH = call.size.h

      startPointerSession(e, {
        onMove: (ev) => {
          const nw = Math.max(
            520,
            Math.min(window.innerWidth, startW + ev.clientX - startX)
          )
          const nh = Math.max(
            360,
            Math.min(window.innerHeight, startH + ev.clientY - startY)
          )
          setSize(activeAgentId, nw, nh)
        },
      })
    },
    [activeAgentId, call, setSize]
  )

  if (!call || !activeAgentId) return null

  const pos = call.fullscreen ? { x: 0, y: 0 } : call.position
  const size = call.fullscreen
    ? { w: window.innerWidth, h: window.innerHeight }
    : call.size
  const mediaState = {
    cameraEnabled: Boolean(call.userCamera),
    micEnabled: Boolean(call.userMic),
    minimized: Boolean(call.minimized),
    screenShareEnabled: Boolean(call.userScreen),
    speakerEnabled: Boolean(call.userAudio),
    uiMode: "call",
  }

  return (
    <>
      <div
        aria-hidden={call.minimized}
        {...focusProps}
        style={{
          position: "fixed",
          left: call.minimized ? -10000 : pos.x,
          top: call.minimized ? 0 : pos.y,
          width: size.w,
          height: size.h,
          background: "rgba(6,6,7,0.95)",
          border: call.fullscreen ? "none" : "1px solid rgba(255,255,255,0.15)",
          borderRadius: call.fullscreen ? 0 : 12,
          zIndex: layeredZIndex,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 30px 80px rgba(0,0,0,0.45)",
          opacity: call.minimized ? 0 : 1,
          pointerEvents: call.minimized ? "none" : "auto",
        }}
      >
        <div
          onPointerDown={onDragStart}
          style={{
            height: 40,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            padding: "0 12px",
            gap: 10,
            cursor: call.fullscreen ? "default" : "grab",
            userSelect: "none",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
            background: "rgba(0,0,0,0.18)",
            touchAction: "none",
          }}
        >
          <span style={{ fontSize: 14 }}>{IC.phone}</span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
                fontSize: 10,
                color: "rgba(255,255,255,0.42)",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
              }}
            >
              Chat App Call
            </div>
            <div
              style={{
                fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
                fontSize: 11,
                color: "white",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {activeAgent ? `Calling ${activeAgent.name}` : "Call unavailable"}
            </div>
          </div>
          <ConnectionBadge indicator={connectionIndicator} />
          <button
            onClick={() => toggleFs(activeAgentId)}
            style={headerBtn}
            title="Fullscreen"
          >
            {call.fullscreen ? IC.shrink : IC.expand}
          </button>
          <button
            onClick={() => toggleMin(activeAgentId)}
            style={headerBtn}
            title="Minimize"
          >
            {IC.dash}
          </button>
          <button
            onClick={() => endCall(activeAgentId)}
            style={headerBtn}
            title="End call"
          >
            {IC.x}
          </button>
        </div>

        <ChatAppEmbedFrame
          agent={activeAgent}
          mediaState={mediaState}
          onConnectionStatusChange={setConnectionStatus}
        />

        {!call.fullscreen && !call.minimized ? (
          <div
            onPointerDown={onResizeStart}
            style={{
              position: "absolute",
              bottom: 0,
              right: 0,
              width: 18,
              height: 18,
              cursor: "nwse-resize",
              background:
                "linear-gradient(135deg, transparent 0 50%, rgba(255,255,255,0.2) 50% 60%, transparent 60% 100%)",
              touchAction: "none",
            }}
          />
        ) : null}
      </div>

      {call.minimized ? (
        <div
          onPointerDown={onDragStart}
          {...focusProps}
          style={{
            position: "fixed",
            left: call.position.x,
            top: call.position.y,
            width: 260,
            height: 76,
            borderRadius: 14,
            border: "1px solid rgba(255,255,255,0.16)",
            overflow: "hidden",
            zIndex: layeredZIndex,
            cursor: "grab",
            background: "rgba(6,6,7,0.95)",
            display: "flex",
            alignItems: "center",
            padding: "0 14px",
            gap: 12,
            touchAction: "none",
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: activeAgent?.color ?? "rgba(255,255,255,0.14)",
              boxShadow: activeAgent
                ? `0 0 20px ${activeAgent.color}55`
                : "none",
              flexShrink: 0,
            }}
          />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
                fontSize: 10,
                color: "rgba(255,255,255,0.45)",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
              }}
            >
              Chat App Call
            </div>
            <div
              style={{
                fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
                fontSize: 12,
                color: "white",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {activeAgent?.name ?? "Unknown agent"}
            </div>
          </div>
          <ConnectionBadge indicator={connectionIndicator} />
          <button
            onClick={(e) => {
              e.stopPropagation()
              toggleMin(activeAgentId)
            }}
            style={miniBtn}
            title="Expand"
          >
            {IC.expand}
          </button>
        </div>
      ) : null}
    </>
  )
}

const headerBtn = {
  background: "none",
  border: "none",
  color: "rgba(255,255,255,0.5)",
  fontSize: 14,
  cursor: "pointer",
  padding: "0 4px",
  fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
}

const miniBtn = {
  width: 28,
  height: 28,
  borderRadius: 8,
  background: "rgba(255,255,255,0.08)",
  border: "none",
  color: "white",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}
